import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Common } from '@thecvlb/design-system';

import { selectOrchestrate } from 'store';

import { BasicSignupStepProps } from 'containers/SignUp/Content/content.types';
import BirthInput from 'shared/form/BirthInput';

import { useAppSelector, useQuery } from 'hooks';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import { PATIENTS_AGE_LIMITS } from 'utils/constants';
import { FlowTypes } from 'utils/enums';
import { checkAgeSupportedOnlyByMedicare, isSupportedAge } from 'utils/helpers';

import Heading from '../../components/Heading';

const Dob: React.FC<BasicSignupStepProps> = ({ moveToStep, selectedFlow }) => {
  const {
    control,
    getValues,
    formState: { isValid },
    reset,
    handleSubmit
  } = useForm({ shouldFocusError: false });

  const navigate = useNavigate();
  const query = useQuery();

  const { user } = useAppSelector(selectOrchestrate);
  const { isMedicareSupportedInState } = user;
  const maxAge = isMedicareSupportedInState
    ? PATIENTS_AGE_LIMITS.MAX_FOR_MEDICARE
    : PATIENTS_AGE_LIMITS.MAX;

  const precessNextStep = (dob: string) => {
    const supportedAge = isSupportedAge(dob);
    const isSupportedOnlyForMedicare = checkAgeSupportedOnlyByMedicare(dob);

    if (supportedAge || (isSupportedOnlyForMedicare && isMedicareSupportedInState)) {
      moveToStep('next');
    } else {
      navigate({ search: `hideProgress=true&subStep=fail-age` });
    }
  };

  const { send, isLoading } = useSubmitOrchestrateForm();

  const onSubmit = () => {
    const dob = getValues('dob');
    if (dob && isValid) {
      send(
        'user',
        {
          dob,
          ...(selectedFlow === FlowTypes.WeightManagementFlowInsuranceMedications && {
            flow: 'benefit'
          })
        },
        () => {
          precessNextStep(dob);
        }
      );
    }
  };

  useEffect(() => {
    if (user.dob) {
      reset({ dob: user.dob });
    }
  }, []);

  const isErrorState = query.get('subStep') === 'fail-age';

  return (
    <form className="flex h-full flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      {isErrorState && (
        <div className="flex h-full flex-col gap-6">
          <h2 className="wm-signup-title text-center" data-testid="header">
            We regret to inform you that you are not suitable for GLP-1 medication.
          </h2>
          <Common.Alert type="warning" colorableBackground>
            GLP-1 medications are indicated for use in adults ages {PATIENTS_AGE_LIMITS.MIN} to{' '}
            {PATIENTS_AGE_LIMITS.MAX}. Our affiliated providers cannot safely prescribe you GLP-1
            medications due to your age.
          </Common.Alert>
        </div>
      )}
      {!isErrorState && (
        <>
          <Heading
            category="About you"
            title={`Great, ${user?.firstName ? user.firstName + ', ' : ''} please enter your date of birth`}
          />
          <BirthInput
            control={control}
            label="Date of birth"
            maxAge={maxAge}
            name="dob"
            size="lg"
          />
          <Common.Button
            className="max-md:mt-auto md:mx-auto"
            color="blue"
            isLoading={isLoading}
            fullWidthOnMobile
          >
            Continue
          </Common.Button>
        </>
      )}
    </form>
  );
};

export default Dob;
